import { useEffect, useState } from "react";

import ApplyForm from "./steps/apply";
import UploadPassport from "./steps/upload-passport";
import PaymentStep from "./steps/payment";

import { vfsApply } from "api/VFS";
import { useTranslation } from "i18n";

import { isUserLoggedIn } from "hooks/LocalStorage";

import "./style.css";

const EntryFee = ({ setClientSecret, setOpenCheckout }) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [data, setBody] = useState({});
  const [passportFile, setPassportFile] = useState(null);
  const [response, setResponse] = useState();
  const [tikkieUrl, setTikkieUrl] = useState("");
  const [formKey, setFormKey] = useState(0);

  const steps = [
    {
      label: t("entryFee.steps.apply"),
      hint: t("entryFee.steps.applyHint"),
    },
    {
      label: t("entryFee.steps.uploadPassport"),
      hint: t("entryFee.steps.uploadHint"),
    },
    {
      label: t("entryFee.steps.payment"),
      hint: t("entryFee.steps.paymentHint"),
    },
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeStep]);

  const applyFVS = async (passport) => {
    if (!isUserLoggedIn()) {
      window.location.hash = "auth-required";
    } else {
      setLoading(true);
      setError("");
      setSuccess("");

      const body = new FormData();
      Object.keys(data).forEach((key) => body.append(key, data[key]));
      body.append("image", passport);

      const response = await vfsApply({
        body,
      });

      if (!response.ok) {
        setError(t("entryFee.errors.generic"));
      } else {
        setTikkieUrl(response.data?.savedEntryFee?.tikkieUrl);
        setResponse(response.data?.savedEntryFee);
        setActiveStep(2);
      }
      setLoading(false);
    }
  };

  const handleStep = () => {
    setActiveStep(1);
  };

  const handleOnSuccessPaymentRequest = (client_secret) => {
    setClientSecret(client_secret);
    setOpenCheckout(true);
  };

  const startNewApplication = () => {
    setActiveStep(0);
    setBody({});
    setPassportFile(null);
    setResponse(undefined);
    setTikkieUrl("");
    setError();
    setSuccess("");
    setLoading(false);
    setFormKey((key) => key + 1);
  };

  return (
    <div className="entry-fee-page">
      <section className="entry-fee-hero">
        <div className="entry-fee-hero__glow" aria-hidden="true" />
        <div className="container entry-fee-hero__content">
          <p className="entry-fee-hero__eyebrow">{t("entryFee.hero.eyebrow")}</p>
          <h1 className="entry-fee-hero__title">{t("entryFee.hero.title")}</h1>
          <p className="entry-fee-hero__subtitle">{t("entryFee.hero.subtitle")}</p>
        </div>
      </section>

      <div className="container entry-fee-shell">
        <div className="entry-fee-board">
          <nav className="entry-fee-steps" aria-label={t("entryFee.hero.eyebrow")}>
            {steps.map((step, index) => {
              const isActive = activeStep === index;
              const isDone = activeStep > index;
              const canGoBack = isDone && activeStep < 2;
              return (
                <button
                  key={step.label}
                  type="button"
                  className={`entry-fee-step${isActive ? " is-active" : ""}${
                    isDone ? " is-done" : ""
                  }${canGoBack ? " is-back" : ""}`}
                  aria-current={isActive ? "step" : undefined}
                  onClick={canGoBack ? () => setActiveStep(index) : undefined}
                >
                  <span className="entry-fee-step__index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span>
                    <span className="entry-fee-step__title">{step.label}</span>
                    <span className="entry-fee-step__hint">{step.hint}</span>
                  </span>
                </button>
              );
            })}
          </nav>

          {activeStep === 0 ? (
            <ApplyForm
              key={formKey}
              setBody={setBody}
              handleStep={handleStep}
              setPassportFile={setPassportFile}
            />
          ) : activeStep === 1 ? (
            <UploadPassport
              applyFVS={applyFVS}
              loading={loading}
              error={error}
              onBack={() => setActiveStep(0)}
              passportFile={passportFile}
              setPassportFile={setPassportFile}
            />
          ) : (
            <PaymentStep
              tikkieUrl={response?.tikkieUrl || tikkieUrl}
              id={response?.entryFeeId}
              handleOnSuccessPaymentRequest={handleOnSuccessPaymentRequest}
              onNewApplication={startNewApplication}
            />
          )}
        </div>

        <aside className="entry-fee-aside">
          <article>
            <h3>{t("entryFee.aside.feeTitle")}</h3>
            <p>{t("entryFee.aside.feeText")}</p>
          </article>
          <article>
            <h3>{t("entryFee.aside.timeTitle")}</h3>
            <p>{t("entryFee.aside.timeText")}</p>
          </article>
          <article>
            <h3>{t("entryFee.aside.validTitle")}</h3>
            <p>{t("entryFee.aside.validText")}</p>
          </article>
        </aside>
      </div>
    </div>
  );
};

export default EntryFee;
